import { Box, Button } from "@mui/material";
import GlobalFilter from "@/components/GlobalFilter";
import CustomSelect from "@/components/CustomSelect";
import { useFormik } from "formik";
import Input from "@/components/Input";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DEF_PAGINATION, replaceUnderScoreAddSpace } from "@/utils/utils";
import Text from "@/components/Text";
import { useMemo, useState } from "react";
import Flex from "@/components/Flex";
import { Option } from "@/constants/common";
import { useProvidersList } from "@/queries/provider/setting/use-providers-list";
import ReferringAgencyFilterSelect from "@/components/app/admin/provider-groups/ReferringAgencyFilterSelect";
import { getStoredRealm } from "@/utils/auth-storage";

interface PatientFilterProps {
  onApply: (v: FilterData) => void;
  patientFilter?: FilterData;
  setPatientFilter: (v: FilterData) => void;
}

type Key = "primary_care_provider";

export type FilterData = {
  primary_care_provider: string;
  referring_agency: Option[];
} & {
  age?: { category: string; val?: number };
  providerId?: { value: string; label: string };
  status?: "active" | "inactive";
};

const initialValues = {
  age: undefined,
  primary_care_provider: "",
  referring_agency: [],
  status: undefined,
};

const PatientFilter = ({
  onApply,
  patientFilter,
  setPatientFilter,
}: PatientFilterProps) => {
  const formik = useFormik<FilterData>({
    initialValues: patientFilter ?? initialValues,
    enableReinitialize: true,
    onSubmit: (v) => {
      setPatientFilter(v);
      onApply(v);
      closeFilter();
    },
  });

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }[]>(
    Object.keys(formik.values).map((it) => ({ [it]: false }))
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeFilter = () => setAnchorEl(null);

  const opened = Boolean(anchorEl);

  const { data: providersList } = useProvidersList({
    pagination: DEF_PAGINATION,
    xTENANTID: getStoredRealm() ?? "",
    enabled: opened,
  });

  function handleExpandClick(k: string, value: boolean) {
    setExpanded((prev) =>
      prev?.map((it) => {
        const curKey = Object.keys(it)[0];

        return Object.keys(it).some((obj) => obj === k)
          ? { [k]: value }
          : { [curKey]: false };
      })
    );
  }

  const OptionsMapper: Record<Key | "status", Option[] | undefined> = {
    primary_care_provider: providersList?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.firstName + " " + it?.lastName,
    })),
    status: STATUS_LIST as any,
  };

  // Count only filters whose values are actually set. The shared
  // useActiveComponentCountController counted `age` as active whenever the
  // object existed (even with empty `val`), so the badge didn't reset until
  // formik.resetForm() ran. (ECRH-4525)
  const activeFilterCount = useMemo(() => {
    const v = formik.values;
    let count = 0;
    if (
      v?.age?.val !== undefined &&
      v?.age?.val !== null &&
      `${v?.age?.val}` !== ""
    )
      count++;
    if (v?.primary_care_provider) count++;
    if (v?.referring_agency?.length) count++;
    if (v?.status) count++;
    return count;
  }, [
    formik.values?.age?.val,
    formik.values?.primary_care_provider,
    formik.values?.referring_agency?.length,
    formik.values?.status,
  ]);

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        width={"18.438rem"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        <Box>
          <Flex width={"100%"} justifyContent={"end"}>
            <Button
              variant="text"
              onClick={() => {
                formik.resetForm();
                setPatientFilter(initialValues);
              }}
            >
              Clear Filter
            </Button>
          </Flex>
          <Accordion
            variant="outlined"
            expanded={expanded[0].age}
            onChange={(_, newExpanded) => handleExpandClick("age", newExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Text>Age</Text>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 1 }}>
              <Flex flexDirection={"column"} gap={1}>
                <CustomSelect
                  value={formik.values.age?.category}
                  options={OPTIONS}
                  onChange={(v) => formik.setFieldValue("age.category", v)}
                />
                <Input
                  textFieldProps={{
                    type: "number",
                    name: "age.val",
                    value: formik.values.age?.val,
                    onChange: formik.handleChange,
                    placeholder: "Enter Value",
                  }}
                />
              </Flex>
            </AccordionDetails>
          </Accordion>
          {Object.keys(formik.values).map((key, ind) => {
            const values = formik.values as any;

            return key === "age" ? (
              <></>
            ) : (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  {key === "referring_agency" ? (
                    <ReferringAgencyFilterSelect
                      value={formik.values.referring_agency}
                      onChange={(v) => formik.setFieldValue(key, v)}
                    />
                  ) : (
                    <CustomSelect
                      value={values[key]}
                      disableSortOptions={key === "status" || undefined}
                      options={OptionsMapper[key as Key]}
                      onChange={(v) => formik.setFieldValue(`${key}`, v)}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
        <Flex width={"100%"} justifyContent={"end"}>
          <Button variant="text" sx={{ color: "gray" }} onClick={closeFilter}>
            Cancel
          </Button>
          <Button variant="text" onClick={formik.submitForm}>
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default PatientFilter;

const OPTIONS = [
  { value: "Less Than (>)", label: "Less Than (<)" },
  { value: "More Than (<)", label: "More Than (>)" },
  { value: "Equal to (=)", label: "Equal to (=)" },
  { value: "Less Than & Equal to (> =)", label: "Less Than & Equal to (< =)" },
  { value: "More Than & Equal to (> =)", label: "More Than & Equal to (> =)" },
];

const STATUS_LIST = [
  { value: undefined, label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
